import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { LogIn, Loader2, Sun, Moon, Shield, Share2, Cpu } from 'lucide-react';
import enactLogo from '../assets/LOGOTIPO-1@2x.png';
import useStore from '../store/useStore';
import { syncProfilesFromServer } from '../utils/connectorConfig';

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { setUser, theme, toggleTheme, incrementConnectorKey } = useStore();

  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(
        `${API_BASE_URL}/auth/login`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username, password }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Invalid credentials');
      }

      const data = await response.json();

      localStorage.setItem('kc_token', data.access_token);
      localStorage.setItem('kc_refresh_token', data.refresh_token);
      localStorage.setItem('username', username);

      const tokenPayload = JSON.parse(atob(data.access_token.split('.')[1]));
      const realmRoles = tokenPayload.realm_access?.roles || [];
      const kcClientRoles = tokenPayload.resource_access?.['realm-management']?.roles || [];
      const roles = [...new Set([...realmRoles, ...kcClientRoles])];

      localStorage.setItem('userRoles', JSON.stringify(roles));

      setUser({ username, email: tokenPayload.email, roles });

      // Pull this account's saved connectors from the backend before
      // navigating, so whichever dashboard loads next shows the same
      // connector config regardless of which browser/device this is.
      await syncProfilesFromServer();
      // Dashboard.jsx (and others) memoize their active connector on this
      // store value and only recompute when it changes - without bumping
      // it here, a page that was already mounted (or whose first render
      // raced the sync above) could keep showing its pre-sync "no
      // connector" state until something else happened to trigger a
      // recompute, which is why a manual reload "fixed" it.
      incrementConnectorKey();

      const isProvider = roles.includes('data-provider');
      const isConsumer = roles.includes('data-consumer');
      const isAdmin   = roles.includes('admin') || roles.includes('realm-admin');

      if (isAdmin) {
        navigate('/dashboard');
      } else if (isProvider) {
        navigate('/provider-dashboard');
      } else if (isConsumer) {
        navigate('/consumer-dashboard');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Invalid username or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex login-page-bg">

      {/* ── Left panel – brand/info (always dark-blue regardless of theme) ── */}
      <div className="login-left-panel hidden lg:flex lg:w-1/2 flex-col justify-between p-12 bg-gradient-to-br from-blue-950 to-slate-900 relative overflow-hidden">

        {/* Decorative circles */}
        <div className="absolute -top-16 -right-16 w-64 h-64 bg-blue-600/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-16 -left-16 w-64 h-64 bg-blue-800/20 rounded-full blur-3xl pointer-events-none" />

        {/* Top – Logo */}
        <div className="relative z-10">
          <div className="flex items-center gap-4 mb-8">
            <img src={enactLogo} alt="ENACT Logo" className="h-14 w-auto object-contain" />
          </div>

          <h2 className="text-3xl font-bold text-white leading-tight mb-4">
            Cognitive Computing<br />Continuum Data Space
          </h2>
          <p className="text-blue-200 text-base leading-relaxed">
            A secure, interoperable data exchange platform enabling seamless collaboration
            across edge, fog and cloud environments — recognised by the IDSA Data Spaces Radar.
          </p>
        </div>

        {/* Middle – Features */}
        <div className="relative z-10 space-y-5">
          <FeatureRow
            icon={<Shield className="w-5 h-5 text-blue-300" />}
            title="Policy-enforced data sharing"
            desc="Fine-grained access control and usage policies govern every data transaction."
          />
          <FeatureRow
            icon={<Share2 className="w-5 h-5 text-blue-300" />}
            title="IDSA-compliant interoperability"
            desc="Built on open standards for seamless cross-organisation data exchange."
          />
          <FeatureRow
            icon={<Cpu className="w-5 h-5 text-blue-300" />}
            title="Cognitive computing continuum"
            desc="Unified data and object management from edge devices to cloud infrastructure."
          />
        </div>

        {/* Bottom – Footer */}
        <div className="relative z-10 border-t border-blue-800/50 pt-6 space-y-4">

          {/* EU flag + funding text */}
          <div className="flex items-start gap-3">
            {/* EU flag SVG */}
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 810 540" className="w-10 h-7 flex-shrink-0 rounded shadow-sm">
              <rect width="810" height="540" fill="#003399"/>
              {/* 12 stars arranged in a circle */}
              {[0,1,2,3,4,5,6,7,8,9,10,11].map(i => {
                const angle = (i * 30 - 90) * Math.PI / 180;
                const cx = 405 + 162 * Math.cos(angle);
                const cy = 270 + 162 * Math.sin(angle);
                return (
                  <polygon key={i}
                    points="0,-18 4.5,-6.3 17.1,-5.6 7.4,3.2 10.5,16 0,8.8 -10.5,16 -7.4,3.2 -17.1,-5.6 -4.5,-6.3"
                    fill="#FFCC00"
                    transform={`translate(${cx},${cy})`}
                  />
                );
              })}
            </svg>
            <div>
              <p className="text-xs text-blue-300 leading-relaxed">
                Funded by the European Union under Grant Agreement No.&nbsp;101135423.
                Views and opinions expressed are those of the authors only.
              </p>
            </div>
          </div>

          {/* Social links + copyright */}
          <div className="flex items-center justify-between">
            <p className="text-xs text-blue-500">
              © {new Date().getFullYear()} ENACT Consortium
            </p>
            <div className="flex items-center gap-3">
              {/* LinkedIn */}
              <a
                href="https://www.linkedin.com/in/enact-horizon-1798122b8/"
                target="_blank"
                rel="noopener noreferrer"
                title="LinkedIn"
                className="text-blue-400 hover:text-white transition-colors"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
              {/* X / Twitter */}
              <a
                href="https://x.com/ENACT_horizon"
                target="_blank"
                rel="noopener noreferrer"
                title="X (Twitter)"
                className="text-blue-400 hover:text-white transition-colors"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.911-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </a>
              {/* YouTube */}
              <a
                href="https://www.youtube.com/@ENACT-INNO"
                target="_blank"
                rel="noopener noreferrer"
                title="YouTube"
                className="text-blue-400 hover:text-white transition-colors"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ── Right panel – login form ── */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-12 login-right-panel relative">

        {/* Theme toggle – top-right */}
        <button
          onClick={toggleTheme}
          title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          className="absolute top-4 right-4 p-2 rounded-lg hover:bg-gray-800 transition-colors login-toggle-btn"
        >
          {theme === 'dark'
            ? <Sun className="w-5 h-5 text-gray-400" />
            : <Moon className="w-5 h-5 text-gray-400" />
          }
        </button>

        {/* Mobile-only logo */}
        <div className="lg:hidden mb-8 text-center">
          <img src={enactLogo} alt="ENACT Logo" className="h-14 w-auto object-contain mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-white">ENACT Dataspace</h1>
          <p className="text-gray-400 text-sm mt-1">Secure data exchange platform</p>
        </div>

        {/* Card */}
        <div className="card w-full max-w-md space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-white">Welcome back</h2>
            <p className="text-gray-400 text-sm mt-1">Sign in to your account to continue</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="input w-full"
                
                required
                autoFocus
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input w-full"
                
                required
              />
            </div>

            {error && (
              <div className="bg-red-900/20 border border-red-800 rounded-lg p-3">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary w-full flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  Signing in...
                </>
              ) : (
                <>
                  <LogIn size={20} />
                  Sign In
                </>
              )}
            </button>
          </form>

          <p className="text-center text-sm text-gray-400">
            Don&apos;t have an account?{' '}
            <Link to="/register" className="text-blue-400 hover:text-blue-300 font-medium transition-colors">
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function FeatureRow({ icon, title, desc }) {
  return (
    <div className="flex items-start gap-4">
      <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-blue-700/40 flex items-center justify-center border border-blue-700/50">
        {icon}
      </div>
      <div>
        <p className="text-sm font-semibold text-white">{title}</p>
        <p className="text-xs text-blue-300 mt-0.5 leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}
